import React, { useState } from 'react';
import { Mail, Send, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSignup = ({ variant = 'full', className = '' }) => {
  const [form, setForm] = useState({ name: '', email: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setError('');

    try {
      await axios.post(`${API_URL}/api/newsletter-signups`, form);
      setStatus('success');
      setForm({ name: '', email: '' });
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
  };

  // Compact variant for footer
  if (variant === 'compact') {
    return (
      <div className={className}>
        <h3 className="text-lg font-semibold text-white mb-3">Newsletter</h3>
        <p className="text-gray-400 text-sm mb-4">
          Get the latest news and updates from The Nimoy Knight Foundation.
        </p>
        
        {status === 'success' ? (
          <div className="flex items-center gap-2 text-green-400 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>You're subscribed!</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-2">
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              placeholder="Your name"
              className="w-full px-3 py-2 bg-[#141416] border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
              data-testid="footer-newsletter-name"
            />
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              placeholder="Your email"
              className="w-full px-3 py-2 bg-[#141416] border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
              data-testid="footer-newsletter-email"
            />
            {status === 'error' && (
              <p className="text-red-400 text-xs">{error}</p>
            )}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black text-sm font-semibold rounded-lg transition-all"
              data-testid="footer-newsletter-submit"
            >
              {status === 'loading' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Subscribe
                </>
              )}
            </button>
          </form>
        )}
      </div>
    );
  }

  // Full variant for pages
  return (
    <section className={`py-20 bg-[#0f0f12] ${className}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#141416] border border-amber-500/20 rounded-3xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center p-3 bg-amber-500/10 rounded-full mb-4">
              <Mail className="w-8 h-8 text-amber-500" />
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">
              Stay Connected
            </h2>
            <p className="text-gray-300 text-lg max-w-xl mx-auto">
              Become the first to get the latest news and updates from The Nimoy Knight Foundation by signing up for our email newsletter!
            </p>
          </div>

          {status === 'success' ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">You're Subscribed!</h3>
              <p className="text-gray-300">
                Thank you for signing up. You'll receive our latest news and updates.
              </p>
              <button
                onClick={() => setStatus('idle')}
                className="mt-6 text-amber-500 hover:text-amber-400 underline"
              >
                Sign up another person
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-4">
              <div>
                <label htmlFor="newsletter-name" className="block text-gray-400 text-sm mb-2">
                  Your Name
                </label>
                <input
                  type="text"
                  id="newsletter-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
                  data-testid="newsletter-name-input"
                />
              </div>
              
              <div>
                <label htmlFor="newsletter-email" className="block text-gray-400 text-sm mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="newsletter-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                  placeholder="Enter your email"
                  className="w-full px-4 py-3 bg-[#0a0a0b] border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-amber-500 transition-colors"
                  data-testid="newsletter-email-input"
                />
              </div>

              {status === 'error' && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-amber-500 hover:bg-amber-600 disabled:bg-amber-500/50 text-black font-bold rounded-xl transition-all hover:scale-[1.02] disabled:hover:scale-100"
                data-testid="newsletter-submit-btn"
              >
                {status === 'loading' ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Subscribe to Newsletter
                  </>
                )}
              </button>

              <p className="text-center text-gray-500 text-xs mt-4">
                🖖 Live Long and Prosper
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewsletterSignup;
